/**
 * BESTIX v5.0 — EventGrid Component
 * Grid layout for displaying events
 */

import { EventCard, type EventCardProps } from './EventCard';
import { fallbackEventImage } from '@/config/hero-images';

// Generic event type that works with both API and card props
interface EventGridEvent {
  id: string | number;
  title?: string;
  name?: string;
  subtitle?: string;
  venue?: string;
  venueName?: string;
  location?: string;
  venueCity?: string;
  venueState?: string;
  dateTime?: string | Date;
  eventDate?: string;
  priceFrom?: number;
  minPriceCents?: number;
  ticketsAvailable?: number;
  availableTickets?: number;
  heatLevel?: 1 | 2 | 3 | 4 | 5;
  isHot?: boolean;
  isFeatured?: boolean;
  imageUrl?: string;
  category?: string;
  href?: string;
}

interface EventGridProps {
  events: EventGridEvent[] | EventCardProps[];
  variant?: 'default' | 'featured';
  columns?: 2 | 3 | 4;
}

// Check if event is already in EventCardProps format
function isEventCardProps(event: EventGridEvent | EventCardProps): event is EventCardProps {
  return 'title' in event && typeof event.title === 'string';
}

// Transform generic event data to EventCardProps
function transformEvent(event: EventGridEvent): EventCardProps {
  // If already in card format, return as-is
  if (isEventCardProps(event)) {
    return event;
  }

  const title = event.title || event.name || 'Event';
  const venue = event.venue || event.venueName || '';
  const location = event.location || 
    (event.venueCity && event.venueState 
      ? `${event.venueCity}, ${event.venueState}` 
      : '');
  const dateTime = event.dateTime || event.eventDate || '';
  const priceFrom = event.priceFrom ?? event.minPriceCents ?? 0;
  const ticketsAvailable = event.ticketsAvailable ?? event.availableTickets;
  const heatLevel = event.heatLevel ?? (event.isHot ? 5 : event.isFeatured ? 4 : 3);

  return {
    id: event.id,
    title,
    subtitle: event.subtitle,
    venue,
    location,
    dateTime,
    priceFrom,
    ticketsAvailable,
    heatLevel: heatLevel as 1 | 2 | 3 | 4 | 5,
    imageUrl: event.imageUrl || fallbackEventImage,
    category: event.category?.toUpperCase() as EventCardProps['category'],
    href: event.href || `/events/${event.id}`,
  };
}

export function EventGrid({ events, variant = 'default', columns = 4 }: EventGridProps) {
  if (events.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No events found</p>
      </div>
    );
  }

  const gridCols = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid grid-cols-1 gap-6 ${gridCols[columns]}`}>
      {events.map((event) => (
        <EventCard 
          key={event.id} 
          {...transformEvent(event)}
          variant={variant === 'featured' ? 'default' : 'default'} 
        />
      ))}
    </div>
  );
}

export default EventGrid;
